import React, { useState, useRef, useCallback } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import { TextField, IconButton, InputAdornment } from '@mui/material';

interface EditableTextInputProps {
  value: any;
  onChange: (value: any) => void;
  onSave: (value: any) => void;
  onCancel: () => void;
  hasValidationError?: boolean;
}

export const EditableTextInput: React.FC<EditableTextInputProps> = ({
  value,
  onChange: _onChange,
  onSave,
  onCancel,
  hasValidationError = false,
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const inputValueRef = useRef(value || '');

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setInputValue(newValue);
      inputValueRef.current = newValue;
    },
    [],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCancel();
      } else if (event.key === 'Enter') {
        onSave(inputValueRef.current);
      }
    },
    [onCancel, onSave],
  );

  const handleBlur = useCallback(() => {
    onSave(inputValueRef.current);
  }, [onSave]);

  const handleClear = useCallback(() => {
    setInputValue('');
    inputValueRef.current = '';
  }, []);

  return (
    <TextField
      value={inputValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      variant='outlined'
      size='small'
      autoFocus
      multiline
      InputProps={{
        endAdornment: inputValue ? (
          <InputAdornment position='end'>
            <IconButton
              size='small'
              onClick={handleClear}
              onMouseDown={(e) => e.preventDefault()}
              sx={{
                padding: '1px',
                marginRight: hasValidationError ? '20px' : '0px',
              }}
            >
              <ClearIcon sx={{ fontSize: '12px' }} />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
      sx={{
        width: 'auto',
        maxWidth: '500px',
        margin: 0,
        '& .MuiOutlinedInput-root': {
          outline: 'none',
          borderBottom: 'none !important',
          borderRadius: 0,
          background: 'transparent',
          minHeight: '24px',
          padding: '0px 4px',
          '&:hover': {
            background: 'transparent',
          },
          '&.Mui-focused': {
            border: 'none',
            outline: 'none',
            background: 'transparent',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
        },
        '& .MuiInputBase-input': {
          padding: '0px',
          background: 'transparent',
          color: 'inherit',
          fontSize: '0.7rem',
          lineHeight: '1.5em',
          minHeight: '24px',
          overflow: 'hidden',
          '&:hover': {
            background: 'transparent',
          },
        },
      }}
    />
  );
};
